import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SearchResultItemDto } from '../../../api';
import { GlobalSearchFacade } from '../../../core/services/global-search.facade';
import { ENTITY_LABELS, ENTITY_ROUTES } from '../../constants/entity-routes';
import { AppSearchBoxComponent } from '../app-search-box/app-search-box.component';

interface ResultGroup {
  label: string;
  items: SearchResultItemDto[];
}

/** Task 10.5: header search bar — federated results grouped by entity type, click navigates. */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [AppSearchBoxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app-global-search.component.html',
  host: { class: 'relative hidden md:block' },
})
export class AppGlobalSearchComponent {
  private readonly facade = inject(GlobalSearchFacade);
  private readonly router = inject(Router);

  readonly open = signal(false);

  protected readonly groups = computed<ResultGroup[]>(() => {
    const byType = new Map<string, SearchResultItemDto[]>();
    for (const item of this.facade.results()) {
      const list = byType.get(item.entityType) ?? [];
      list.push(item);
      byType.set(item.entityType, list);
    }

    return Array.from(byType.entries()).map(([entityType, items]) => ({
      label: ENTITY_LABELS[entityType as keyof typeof ENTITY_LABELS] ?? entityType,
      items,
    }));
  });

  onSearch(query: string): void {
    this.facade.search(query);
    this.open.set(query.trim().length >= 2);
  }

  onBlur(): void {
    // Delay so a click on a result (which also blurs the input) still registers.
    setTimeout(() => this.open.set(false), 150);
  }

  select(item: SearchResultItemDto): void {
    const base = ENTITY_ROUTES[item.entityType];
    this.open.set(false);
    if (base) {
      void this.router.navigate([base, item.entityId]);
    }
  }
}
